import { shallowRef, triggerRef } from "vue";
import { ThemeColors } from "./types/skyward/themecolors";

export function parseHexColor(hex: string)
{
    // validate hex string
    const allDigitsAndLettersRegex = /[^0-9a-f]/gi;
    let safeHex = String(hex).replace(allDigitsAndLettersRegex, '');
    if (safeHex.length < 6)
    {
        safeHex = safeHex[0] + safeHex[0] + safeHex[1] + safeHex[1] + safeHex[2] + safeHex[2];
    }

    const color =
    {
        red: parseHexPart(safeHex, 0),
        green: parseHexPart(safeHex, 2),
        blue: parseHexPart(safeHex, 4),

        luminate: function (lum: number, minChange: number)
        {
            var safeLum = lum || 0;
            this.red = luminatePart(this.red, safeLum, minChange);
            this.green = luminatePart(this.green, safeLum, minChange);
            this.blue = luminatePart(this.blue, safeLum, minChange);
        },

        toHex: function ()
        {
            return "#" + valToHex(this.red) + valToHex(this.green) + valToHex(this.blue);
        }
    };

    return color;
}

export function changeAlpha (rgbaString: string, newAlpha: number)
{
    if (rgbaString.indexOf("#") === 0)
    {
        return hexToRGBA(rgbaString, newAlpha);
    }

    const rgbaValues = rgbaString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);

    if (rgbaValues)
    {
        return `rgba(${rgbaValues[1]}, ${rgbaValues[2]}, ${rgbaValues[3]}, ${newAlpha})`;
    } else
    {
        // Handle the case where the input string is not a valid rgba string
        return rgbaString;
    }
};

export function hexToRGBA (hex: string, alpha: number = 1)
{
    // Remove the # if it exists
    hex = hex.replace('#', '');

    // Check if the hex code is 3 or 6 characters long
    if (hex.length !== 3 && hex.length !== 6)
    {
        throw new Error('Invalid HEX color.');
    }

    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    if (hex.length === 3)
    {
        hex = hex.split('').map(c => c + c).join('');
    }

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Return the RGBA string
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function rgbToHex(rgb: string)
{
    const digitStringsRegex = /\d+/g;
    const colorValues = [...rgb.matchAll(digitStringsRegex)].map(match => match[0]);

    const r = parseInt(colorValues[0]);
    const g = parseInt(colorValues[1]);
    const b = parseInt(colorValues[2]);
    const a = colorValues[3] != undefined ? parseInt(colorValues[3]) : 1;

    const redHex = valToHex(r);
    const greenHex = valToHex(g);
    const blueHex = valToHex(b);

    const valuesObj =
    {
        Red: redHex,
        Green: greenHex,
        Blue: blueHex,
        Alpha: a,
        Value: `#${redHex}${greenHex}${blueHex}`
    };

    return valuesObj;
}

function valToHex(colorValue: number)
{
    const localHex = colorValue.toString(16);
    return localHex.padStart(2, "0");
}

function parseHexPart(hex: string, startIndex: number)
{
    return parseInt(hex.substr(startIndex, 2), 16);
}

function luminatePart(colorValue: number, lum: number, minChange: number)
{
    let change = colorValue * lum;
    if (Math.abs(change) < minChange)
    {
        change = change < 0 ? -1 * minChange : minChange;
    }

    return Math.round(Math.min(Math.max(0, colorValue + change), 255));
}

export function setColorAlpha(color: string, alpha: number)
{
    const rgba = getColorRGB(color);
    if (!rgba)
    {
        return color;
    }
    return `rgba(${rgba.r},${rgba.g},${rgba.b},${alpha})`;
}

/**
 * Given a valid css color string, determine if is a Dark color
 * @param color Valid css color string [Ex. #ffffff OR rgba(255, 255, 255)]
 * @returns true if the color is Dark
 */
export function isDarkColor(color: string)
{
    const rgba = getColorRGB(color);
    if (!rgba)
    {
        return false;
    }

    // Calculate luminance
    const luminance = (0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b) / 255;

    return luminance < 0.5; // Dark if luminance < 0.5
}

function getColorRGB(color: string): { r: number, g: number, b: number, a?: number } | undefined
{
    const start = color.slice(0, 1);
    if (start === '#')
    {
        return splitHEX(color);
    }
    if (start.toLowerCase() === 'r')
    {
        return splitRGB(color);
    }
    return undefined;

    function splitHEX(hex: string)
    {
        let c;
        if (hex.length === 4)
        {
            c = (hex.replace('#', '')).split('');
            return {
                r: hex2dec((c[0] + c[0])),
                g: hex2dec((c[1] + c[1])),
                b: hex2dec((c[2] + c[2]))
            };
        }
        return {
            r: hex2dec(hex.slice(1, 3)),
            g: hex2dec(hex.slice(3, 5)),
            b: hex2dec(hex.slice(5))
        };

        function hex2dec(v: string)
        {
            return parseInt(v, 16)
        };
    };

    function splitRGB(rgb: string)
    {
        let c: any = (rgb.slice(rgb.indexOf('(') + 1, rgb.indexOf(')'))).split(',');
        let supportsAlpha = false;
        let obj: any;
        c = c.map(function (n: string, i: number)
        {
            return (i !== 3) ? parseInt(n, 10) : supportsAlpha = true, parseFloat(n);
        });

        obj =
        {
            r: c[0],
            g: c[1],
            b: c[2]
        };
        if (supportsAlpha)
        {
            obj.a = c[3];
        }
        return obj;
    }
}

export function makeThemeColorGetter(elem: HTMLElement): ThemeColors
{
    const computedView = getComputedStyle(elem);
    return new Proxy(computedView, {
        get(_target, key: string) {
            if (!key.includes("-")) {
                key = "--sky-" + key.toDisplayName().toLowerCase().replace(/ /g, "-");
            }
            return computedView.getPropertyValue(key as any);
        }
    }) as unknown as ThemeColors;
}

const globalThemeColors = makeThemeColorGetter(document.body);
export const activeThemeRef = shallowRef(globalThemeColors);

$(document.body).on("themeSwap", () =>
{
    triggerRef(activeThemeRef);
});
